import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SEOHead from './SEOHead';
import { 
  Monitor, 
  Cloud, 
  Shield, 
  Server, 
  Headphones, 
  HardDrive,
  CheckCircle,
  ArrowRight,
  Phone,
  Zap,
  Clock,
  Users,
  Settings,
  Database,
  Lock,
  Wifi,
  Globe,
  Laptop,
  Smartphone,
  X
} from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      id: 'consulting',
      icon: Monitor,
      title: 'IT Consulting & Support',
      description: 'One little bump in technology shouldn\'t cause downtime. To avoid it, use our IT emergency support services!',
      image: '/images/optimized/pexels-1181405.webp',
      features: [
        'Full analysis of your system network',
        'Health assessment of current systems',
        'Custom IT strategies for your budget',
        'Increased functionality and reliability',
        'Enhanced security measures',
        'Long-term IT planning and roadmap',
        'Vendor management and negotiations',
        'Technology procurement guidance'
      ],
      details: 'At King of IT, our IT consulting services give you a comprehensive analysis of your system network. We assess how healthy your current system is performing and identify areas for improvement. Our experienced consultants work with you to create a plan that fits your business budget and needs, whether you\'re a small startup or an established enterprise.',
      benefits: [
        'Reduce IT-related downtime by up to 90%',
        'Lower total cost of IT ownership',
        'Access to enterprise-level expertise',
        'Strategic technology planning'
      ]
    },
    {
      id: 'remote',
      icon: Headphones,
      title: 'Remote Office Support',
      description: 'Whatever you are, our remote IT support is designed to resolve tech issues for your team regardless of location.',
      image: '/images/optimized/pexels-1181354.webp',
      features: [
        'Location-independent support',
        'Quick issue resolution',
        'Real-time troubleshooting',
        'Screen sharing assistance',
        'Remote software installation',
        '24/7 availability for emergencies',
        'Multi-platform support (Windows, Mac, Linux)',
        'Mobile device management'
      ],
      details: 'Our remote IT support team is equipped with the latest tools and technologies to provide seamless support to your team, no matter where they are located. Whether your employees work from home, the office, or on the road, we ensure they have the IT support they need to stay productive.',
      benefits: [
        'Support available anywhere, anytime',
        'Faster resolution than on-site visits',
        'Cost-effective for distributed teams',
        'Comprehensive remote monitoring'
      ]
    },
    {
      id: 'managed',
      icon: Server,
      title: 'Managed Services',
      description: 'Most types of businesses rely on the Internet or a local computer network. We can fix both!',
      image: '/images/optimized/pexels-5380642.webp',
      features: [
        'Network monitoring & maintenance',
        'Proactive issue prevention',
        'Regular system updates and patches',
        'Performance optimization',
        'Backup management',
        'Security patch deployment',
        'Help desk support',
        'Monthly reporting and reviews'
      ],
      details: 'Our managed services provide comprehensive IT support that keeps your business running smoothly. We monitor your systems 24/7, address issues before they become problems, and ensure your technology infrastructure is always optimized for peak performance. Think of us as your complete IT department.',
      benefits: [
        'Predictable monthly IT costs',
        'Proactive vs reactive support',
        'Reduced security vulnerabilities',
        'Improved system performance'
      ]
    },
    {
      id: 'continuity',
      icon: Shield,
      title: 'Backup & Disaster Recovery',
      description: 'Protect your business from data loss with comprehensive backup solutions and disaster recovery planning.',
      image: '/images/optimized/pexels-5380664.webp',
      features: [
        'Cloud backup to Canadian data centers',
        'Server & workstation backup',
        'Microsoft 365 backup (email, OneDrive, SharePoint)',
        'Ransomware recovery solutions',
        'Disaster recovery planning',
        'Recovery time objectives (RTO)',
        'Recovery point objectives (RPO)',
        'Regular testing and verification'
      ],
      details: 'Don\'t let ransomware, hardware failure, or natural disasters destroy your business. Our backup and disaster recovery solutions protect your data with automated cloud backups, immutable storage that ransomware can\'t encrypt, and tested recovery procedures that get you back online fast.',
      benefits: [
        'Recover from ransomware without paying ransom',
        'Minimize downtime after any disaster',
        'Meet compliance requirements',
        'Peace of mind with tested recovery plans'
      ],
      link: '/backup-disaster-recovery-toronto'
    },
    {
      id: 'outsourced',
      icon: Cloud,
      title: 'Outsourced IT Service',
      description: 'Eventually, any business will need its data to be virtualized. We offer the best cloud system solutions in the industry!',
      image: '/images/optimized/pexels-1181467.webp',
      features: [
        'Cloud migration services',
        'Virtual infrastructure setup',
        'SaaS implementation',
        'Cloud security management',
        'Cost optimization',
        'Scalable solutions',
        'Microsoft 365 administration',
        'Google Workspace management'
      ],
      details: 'We offer the best cloud system solutions in the industry! Our outsourced IT services give you access to enterprise-level technology and expertise without the overhead of maintaining an in-house IT department. We become your dedicated IT team, handling everything from day-to-day support to strategic planning.',
      benefits: [
        'Access full IT team for fraction of cost',
        'Scale services as you grow',
        'Latest technology and expertise',
        'Focus on your core business'
      ]
    },
    {
      id: 'hardware',
      icon: HardDrive,
      title: 'New & Refurbished Hardware',
      description: 'We work with multiple kinds of businesses, so we have a set of niche-specific IT systems at our command.',
      image: '/images/optimized/pexels-4792733.webp',
      features: [
        'Quality hardware sourcing',
        'Certified refurbished options',
        'Hardware installation & setup',
        'Equipment lifecycle management',
        'Warranty support',
        'Cost-effective solutions',
        'E-waste recycling programs',
        'Hardware maintenance plans'
      ],
      details: 'We work with multiple kinds of businesses, so we have a set of niche-specific IT systems at our command. Whether you need new equipment or quality refurbished hardware, we can provide solutions that fit your budget and requirements. We also help with hardware lifecycle management, ensuring you get the most value from your IT investments.',
      benefits: [
        'Save up to 50% with refurbished',
        'Quality guaranteed equipment',
        'Proper setup and configuration',
        'Ongoing hardware support'
      ]
    }
  ];

  const additionalServices = [
    { 
      icon: Lock, 
      title: 'Cybersecurity', 
      description: 'Protect your business from threats',
      fullDescription: 'In today\'s digital landscape, cybersecurity isn\'t optional—it\'s essential. King of IT provides comprehensive security solutions that protect your business from evolving cyber threats, data breaches, and malicious attacks.',
      features: [
        'Advanced threat detection and prevention',
        'Firewall configuration and management',
        'Endpoint security for all devices',
        'Email security and spam filtering',
        'Security awareness training for employees',
        'Vulnerability assessments and penetration testing',
        'Incident response planning',
        '24/7 security monitoring'
      ],
      benefits: [
        'Protect sensitive business and customer data',
        'Technical controls supporting PIPEDA and privacy compliance',
        'Reduce risk of costly data breaches',
        'Build customer trust and confidence'
      ],
      image: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1920&q=80'
    },
    { 
      icon: Wifi, 
      title: 'Network Setup', 
      description: 'Design and implement networks',
      fullDescription: 'A robust network infrastructure is the backbone of your business operations. Our network experts design, implement, and maintain high-performance networks that keep your team connected and productive.',
      features: [
        'Network architecture design and planning',
        'Wired and wireless network installation',
        'Router and switch configuration',
        'Network security implementation',
        'VPN setup for remote access',
        'Network performance optimization',
        'Bandwidth management',
        'Network documentation and mapping'
      ],
      benefits: [
        'Fast, reliable connectivity throughout your office',
        'Secure remote access for distributed teams',
        'Scalable infrastructure that grows with you',
        'Reduced network downtime and interruptions'
      ],
      image: '/images/optimized/pexels-2881232.webp'
    },
    { 
      icon: Database, 
      title: 'Data Management', 
      description: 'Organize and protect your data',
      fullDescription: 'Your data is one of your most valuable business assets. We help you organize, secure, and leverage your data effectively with comprehensive data management solutions that ensure accessibility and protection.',
      features: [
        'Data backup and disaster recovery',
        'Cloud storage solutions',
        'Database design and optimization',
        'Data migration services',
        'Automated backup scheduling',
        'Data encryption and security',
        'Archive management',
        'Compliance data handling'
      ],
      benefits: [
        'Never lose critical business data',
        'Quick recovery from any disaster',
        'Easy access to historical records',
        'Compliant data storage and retention'
      ],
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80'
    },
    { 
      icon: Globe, 
      title: 'VoIP Solutions', 
      description: 'Modern phone systems',
      fullDescription: 'Upgrade your business communications with modern VoIP phone systems. We implement feature-rich, cost-effective voice solutions that integrate seamlessly with your existing infrastructure and support remote work.',
      features: [
        'Cloud-based phone system setup',
        'Auto-attendant and call routing',
        'Voicemail to email transcription',
        'Video conferencing integration',
        'Mobile app for remote calling',
        'Call recording and analytics',
        'Integration with CRM systems',
        'Multi-location unified communications'
      ],
      benefits: [
        'Reduce phone bills by up to 60%',
        'Professional image with enterprise features',
        'Work from anywhere with mobile integration',
        'Scale easily as your team grows'
      ],
      image: 'https://images.unsplash.com/photo-1596524430615-b46475ddff6e?auto=format&fit=crop&w=1920&q=80'
    },
    { 
      icon: Laptop, 
      title: 'Workstation Setup', 
      description: 'Configure employee computers',
      fullDescription: 'Get your team up and running quickly with professional workstation setup services. We configure computers, install software, and ensure everything is optimized for productivity from day one.',
      features: [
        'New computer setup and configuration',
        'Software installation and licensing',
        'Email and productivity suite setup',
        'Security software deployment',
        'Data migration from old computers',
        'Peripheral setup (printers, monitors)',
        'User profile configuration',
        'Performance optimization'
      ],
      benefits: [
        'Employees productive from day one',
        'Standardized, secure configurations',
        'Proper software licensing compliance',
        'Reduced IT support tickets'
      ],
      image: 'https://images.unsplash.com/photo-1593062096033-9a26b09da705?auto=format&fit=crop&w=1920&q=80'
    },
    { 
      icon: Smartphone, 
      title: 'Mobile Management', 
      description: 'Secure mobile devices',
      fullDescription: 'Secure and manage your company\'s mobile devices with enterprise-grade mobile device management (MDM). Protect business data on smartphones and tablets while enabling workforce mobility.',
      features: [
        'Mobile Device Management (MDM) setup',
        'BYOD policy implementation',
        'Remote device wiping capability',
        'App deployment and management',
        'Mobile security policies',
        'Device tracking and inventory',
        'Secure email on mobile devices',
        'Compliance enforcement'
      ],
      benefits: [
        'Secure company data on all devices',
        'Enable safe BYOD programs',
        'Remotely manage lost or stolen devices',
        'Maintain compliance across mobile fleet'
      ],
      image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1920&q=80'
    }
  ];

  const [selectedService, setSelectedService] = useState(null);

  return (
    <>
      <SEOHead
        title="IT Services Toronto | Computer Repair, Network Setup, Cybersecurity | King of IT"
        description="Professional IT services in Toronto: managed IT, Microsoft 365, cybersecurity, cloud migration, and 24/7 support. Serving 500+ GTA businesses with 31 years experience. Call 647-951-3653."
        path="/services"
      />
      
      <main className="py-0" role="main">
        {/* Hero Section */}
        <section className="bg-gradient-navy text-white py-16 lg:py-20" aria-labelledby="services-hero-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30">
              IT Services Toronto
            </Badge>
            <h1 id="services-hero-heading" className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6">
              Professional <span className="text-gradient-gold">IT Services in Toronto</span>
            </h1>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              King of IT provides comprehensive IT solutions for businesses across the Greater Toronto Area. From computer repair and network setup to managed IT services and cybersecurity, we have the expertise to keep your business running smoothly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="services-hero-cta">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                  Get Free IT Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:6479513653" data-testid="services-hero-phone">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10">
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Services List */}
        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-16 lg:space-y-24">
              {services.map((service, index) => (
                <div 
                  key={service.id}
                  id={service.id}
                  className="scroll-mt-24"
                >
                  <div className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                    index % 2 === 1 ? '' : ''
                  }`}>
                    {/* Image */}
                    <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <div className="rounded-2xl overflow-hidden shadow-xl">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="absolute -bottom-4 -right-4 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-gold rounded-2xl -z-10 opacity-20" />
                    </div>
                    
                    {/* Content */}
                    <div className={`space-y-4 sm:space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-gold flex items-center justify-center shadow-gold">
                        <service.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                      </div>
                      <h2 className="font-display font-bold text-2xl sm:text-3xl text-foreground">
                        {service.title}
                      </h2>
                      <p className="text-base sm:text-lg text-muted-foreground">
                        {service.description}
                      </p>
                      <p className="text-muted-foreground leading-relaxed text-sm sm:text-base">
                        {service.details}
                      </p>
                      
                      {/* Features Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 pt-2 sm:pt-4">
                        {service.features.map((feature) => (
                          <div key={feature} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-foreground">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      {/* Benefits */}
                      <div className="bg-muted/50 rounded-xl p-4 sm:p-6">
                        <h4 className="font-semibold text-foreground mb-3 text-sm sm:text-base">Key Benefits:</h4>
                        <ul className="space-y-2">
                          {service.benefits.map((benefit) => (
                            <li key={benefit} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                              <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <Link to="/contact">
                        <Button className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 mt-4">
                          Get Started
                          <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                      </Link>
                      {service.link && (
                        <Link to={service.link} className="ml-3">
                          <Button variant="outline" className="mt-4 border-primary text-primary hover:bg-primary hover:text-white">
                            Learn More
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-16 lg:py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 lg:mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                More Services
              </Badge>
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground">
                Additional <span className="text-gradient-gold">IT Solutions</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
              {additionalServices.map((service) => (
                <Card 
                  key={service.title} 
                  className="p-4 sm:p-6 text-center hover:shadow-card-hover hover-lift cursor-pointer transition-all"
                  onClick={() => setSelectedService(service)}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 rounded-lg bg-primary/10 flex items-center justify-center">
                    <service.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-xs sm:text-sm mb-1">{service.title}</h3>
                  <p className="text-muted-foreground text-xs hidden sm:block">{service.description}</p>
                  <Button variant="link" className="text-primary text-xs mt-2 p-0 h-auto">
                    Learn More →
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Detail Modal */}
        {selectedService && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedService(null)}
          >
            <div 
              className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header with Image */}
              <div className="relative h-48 sm:h-64 overflow-hidden rounded-t-2xl">
                <img 
                  src={selectedService.image} 
                  alt={selectedService.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <button 
                  onClick={() => setSelectedService(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-4 left-6 right-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-lg bg-gradient-gold flex items-center justify-center">
                      <selectedService.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="font-display font-bold text-2xl sm:text-3xl text-white">
                      {selectedService.title}
                    </h2>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 sm:p-8">
                <p className="text-muted-foreground text-base sm:text-lg mb-6">
                  {selectedService.fullDescription}
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Features */}
                  <div>
                    <h3 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      What We Offer
                    </h3>
                    <ul className="space-y-2">
                      {selectedService.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Benefits */}
                  <div>
                    <h3 className="font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Key Benefits
                    </h3>
                    <ul className="space-y-3">
                      {selectedService.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-primary font-semibold text-xs">{index + 1}</span>
                          </div>
                          <span className="text-muted-foreground">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                  <Link to="/contact" className="flex-1">
                    <Button className="w-full bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
                      Get a Free Quote
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <a href="tel:6479513653" className="flex-1">
                    <Button variant="outline" className="w-full">
                      <Phone className="w-4 h-4 mr-2" />
                      Call 647-951-3653
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dedicated Toronto Service Pages */}
        <section className="py-16 lg:py-20 bg-white border-t border-slate-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-3">
                IT Services by <span className="text-gradient-gold">Specialty</span>
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">Explore our dedicated service pages for detailed information, pricing, and FAQs</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/services/managed-it-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Server className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Managed IT Services</h3>
                <p className="text-sm text-muted-foreground mt-1">24/7 monitoring & proactive maintenance</p>
              </Link>
              <Link to="/services/it-support-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Headphones className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">IT Support</h3>
                <p className="text-sm text-muted-foreground mt-1">Fast on-site & remote assistance</p>
              </Link>
              <Link to="/services/cybersecurity-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Shield className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Cybersecurity</h3>
                <p className="text-sm text-muted-foreground mt-1">Threat monitoring & protection</p>
              </Link>
              <Link to="/services/microsoft-365-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Monitor className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Microsoft 365</h3>
                <p className="text-sm text-muted-foreground mt-1">Setup, migration & management</p>
              </Link>
              <Link to="/services/cloud-solutions-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Cloud className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Cloud Solutions</h3>
                <p className="text-sm text-muted-foreground mt-1">Migration & cloud management</p>
              </Link>
              <Link to="/services/network-setup-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Wifi className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Network Setup</h3>
                <p className="text-sm text-muted-foreground mt-1">Design, cabling & WiFi</p>
              </Link>
              <Link to="/services/emergency-it-support-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Zap className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Emergency IT</h3>
                <p className="text-sm text-muted-foreground mt-1">24/7 rapid response</p>
              </Link>
              <Link to="/services/small-business-it-toronto" className="group p-5 rounded-xl border border-slate-200 hover:border-yellow-500 hover:shadow-md transition-all">
                <Users className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">Small Business IT</h3>
                <p className="text-sm text-muted-foreground mt-1">Affordable plans for 10-200 employees</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-gradient-navy text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mb-4 sm:mb-6">
              Need Help Choosing the Right Service?
            </h2>
            <p className="text-gray-300 text-base sm:text-lg mb-6 sm:mb-8">
              Our team is ready to help you find the perfect IT solution for your business needs. 
              Get a free consultation and let us analyze your requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:6479513653">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  647-951-3653
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ServicesPage;
